import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import useModal from '@/hooks/useModal'
import { useSearchSignupShippers } from '@/services/shipper/useShipperQueries'
import { ShipperSearchItem } from '@/services/shipper/types'
import MAlert from '@/utils/modal/MAlert'
import { VStack, Modal } from '@musinsa/mwms-ds'
import styled from 'styled-components'

// *** 주의사항 ***
// trigger 은 onClick props를 받아서 처리 가능해야 합니다.

interface ShipperSelectModalProps {
  trigger: React.ReactNode
  title?: string
  shipperName?: string
  onSelect: (params: ShipperSearchItem) => void
}

function ShipperSelectModal({
  title = '소속 찾기',
  shipperName: sprName,
  trigger,
  onSelect,
}: ShipperSelectModalProps) {
  const { open, onOpenModal, onCloseModal } = useModal()

  const [query, setQuery]         = useState(sprName ?? '')
  const [selectedIdx, setSelectedIdx] = useState(-1)
  const [hasSearched, setHasSearched] = useState(false)

  // 회원가입(비로그인) 전용 검색 API(LOGISPRD-8888 /api/v1/auth/shippers). enabled:false → refetch 시점에만 조회.
  // 1.0 화주 검색 경로는 지웠다 — 쓰던 IV02·IV03·RP06 은 ShipperCodeSelect 로 옮겼다(LOGISPRD-15411).
  const active = useSearchSignupShippers(query)
  const list = active.data?.list ?? []

  // Enter(또는 검색 트리거) 수동 조회.
  const onSearch = useCallback(() => {
    if (!query.trim()) return
    setSelectedIdx(-1)
    setHasSearched(true)
    active.refetch()
  }, [query, active])

  // 입력 즉시(디바운스) 자동 조회.
  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (!q) {
      setHasSearched(false)
      setSelectedIdx(-1)
      return
    }
    const timer = setTimeout(() => {
      setSelectedIdx(-1)
      setHasSearched(true)
      active.refetch()
    }, 150)
    return () => clearTimeout(timer)
    // active.refetch 은 쿼리 인스턴스에 안정적이므로 query 변화에만 반응
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open])

  // 모달 재진입 시 현재 소속명(sprName) 기준으로 내부 상태를 초기화한다. (LOGISPRD-9390)
  // open 이 false→true 로 전환될 때마다 실행되어, 닫혔다 다시 열 때 이전 검색어/선택이
  // 잔존하지 않고 항상 현재 저장된 소속명이 표시되도록 한다.
  useEffect(() => {
    if (!open) return
    setQuery(sprName ?? '')
    setSelectedIdx(-1)
    setHasSearched(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleClose = useCallback(() => {
    onCloseModal()
  }, [onCloseModal])

  const onSubmit = useCallback(() => {
    if (selectedIdx === -1) return MAlert.show('소속을 선택해주세요')
    onSelect(list[selectedIdx])
    handleClose()
  }, [selectedIdx, list, onSelect, handleClose])

  return (
    <>
      {trigger
        ? React.cloneElement(trigger as ReactElement, { onClick: onOpenModal })
        : null}
      {open && (
        <Modal
          open
          size="sm"
          title={title}
          closable={false}
          customFooter={
            <>
              <CancelBtn type="button" onClick={handleClose}>취소</CancelBtn>
              <ConfirmBtn type="button" disabled={selectedIdx === -1} onClick={onSubmit}>선택완료</ConfirmBtn>
            </>
          }
          onClose={handleClose}
        >
          <VStack gap={16}>
            {/* 검색 입력 */}
            <SearchWrap>
              <SearchSvg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </SearchSvg>
              <SearchInput
                placeholder="화주명 검색"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setHasSearched(false)
                  setSelectedIdx(-1)
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') onSearch() }}
              />
              {query && (
                <ClearBtn
                  onClick={() => { setQuery(''); setHasSearched(false); setSelectedIdx(-1) }}
                  aria-label="검색어 지우기"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </ClearBtn>
              )}
            </SearchWrap>

            {/* 결과 테이블 */}
            <TableBorder>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <Th style={{ width: 48, textAlign: 'center' }} />
                    <Th>화주</Th>
                    <Th>화주코드</Th>
                  </tr>
                </thead>
                <tbody>
                  {/* 검색 전 — 빈 상태 */}
                  {!hasSearched && (
                    <tr>
                      <td colSpan={3}>
                        <EmptyState>
                          <EmptyIconWrap>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                              <ellipse cx="14" cy="8" rx="8" ry="3.5" stroke="#bdbdbd" strokeWidth="1.4" fill="none" />
                              <path d="M6 8v5c0 1.93 3.58 3.5 8 3.5s8-1.57 8-3.5V8" stroke="#bdbdbd" strokeWidth="1.4" fill="none" />
                              <path d="M6 13v5c0 1.93 3.58 3.5 8 3.5s8-1.57 8-3.5v-5" stroke="#bdbdbd" strokeWidth="1.4" fill="none" />
                            </svg>
                          </EmptyIconWrap>
                          <EmptyText>화주명을 검색해 주세요.</EmptyText>
                        </EmptyState>
                      </td>
                    </tr>
                  )}

                  {/* 결과 없음 — 로딩(fetching) 중엔 숨겨 깜빡임 방지 */}
                  {hasSearched && !active.isFetching && list.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <NoResultBox>
                          <svg viewBox="0 0 24 24" fill="none" stroke="#f31110" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4m0 4h.01" />
                          </svg>
                          <span>조회된 결과가 존재하지 않습니다.</span>
                        </NoResultBox>
                      </td>
                    </tr>
                  )}

                  {/* 결과 있음 */}
                  {hasSearched && list.map((item, idx) => (
                    <ResultRow
                      key={item.shipper_code}
                      $selected={idx === selectedIdx}
                      onClick={() => setSelectedIdx(idx)}
                    >
                      <td style={{ textAlign: 'center', padding: 0, height: 36 }}>
                        <input
                          type="radio"
                          checked={idx === selectedIdx}
                          onChange={() => setSelectedIdx(idx)}
                          style={{ cursor: 'pointer', accentColor: '#245eff', width: 16, height: 16 }}
                        />
                      </td>
                      <td style={{ padding: '0 12px', fontSize: 14, color: '#000', height: 36 }}>{item.shipper_name}</td>
                      <td style={{ padding: '0 12px', fontSize: 14, color: '#000', height: 36 }}>{item.shipper_code}</td>
                    </ResultRow>
                  ))}
                </tbody>
              </table>
            </TableBorder>
          </VStack>
        </Modal>
      )}
    </>
  )
}

/* ── Styled Components ── */

const FooterBtn = styled.button`
  min-width: 56px;
  height: 40px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;
`

const CancelBtn = styled(FooterBtn)`
  background: #fff;
  border: 1px solid #e0e0e0;
  color: #000;

  &:hover {
    background: #f7f7f7;
  }
`

const ConfirmBtn = styled(FooterBtn)`
  background: #000;
  border: 1px solid #000;
  color: #fff;

  &:not(:disabled):hover {
    background: #222;
  }

  &:disabled {
    background: rgba(138, 138, 138, 0.1);
    border-color: transparent;
    color: rgba(0, 0, 0, 0.2);
    cursor: not-allowed;
  }
`

const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SearchSvg = styled.svg`
  position: absolute;
  left: 12px;
  width: 20px;
  height: 20px;
  color: #adb5bd;
  pointer-events: none;
  flex-shrink: 0;
`

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  background: #fff;
  color: #000;

  &::placeholder {
    color: #adb5bd;
  }

  &:focus {
    border-color: #000;
  }
`

const ClearBtn = styled.button`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #c4c4c4;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #fff;

  &:hover {
    background: #adadad;
  }
`

const TableBorder = styled.div`
  border: 1px solid #ebebeb;
  border-radius: 4px;
  /* 검색 상태(빈 상태/결과 없음/결과 목록)와 무관하게 모달 크기를 고정한다. (LOGISPRD-9386) */
  height: 280px;
  overflow-y: auto;
`

const Th = styled.th`
  padding: 0 12px;
  height: 36px;
  border-bottom: 1px solid #ebebeb;
  font-weight: 500;
  font-size: 14px;
  text-align: left;
  color: #666;
  background: #f8f9fc;
  /* 결과 스크롤 시에도 헤더 고정 */
  position: sticky;
  top: 0;
  z-index: 1;
`

const EmptyState = styled.div`
  padding: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const EmptyIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`

const EmptyText = styled.span`
  font-size: 14px;
  color: #888;
  line-height: 1.4;
`

const NoResultBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff2f2;
  border: 1px solid #ffd0d0;
  border-radius: 4px;
  padding: 10px 14px;
  margin: 16px;
  font-size: 14px;
  color: #f31110;
  line-height: 1.4;
`

const ResultRow = styled.tr<{ $selected: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#f5f7ff' : 'transparent')};
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $selected }) => ($selected ? '#eff3ff' : '#fafafa')};
  }
`

export default ShipperSelectModal
